import { useEffect, useMemo, useRef, useState } from "react"

import { useDrag } from "react-dnd"

import { ArrowDownIcon, ArrowPathRoundedSquareIcon, ArrowTopRightOnSquareIcon, ArrowUpIcon, ExclamationCircleIcon, ExclamationTriangleIcon, PencilSquareIcon } from "@heroicons/react/24/outline"
import { useQueryClient } from "@tanstack/react-query"

import Modal from "../../layouts/Modal"
import { DragTypes } from "../../models/DragTypes"
import { ILead, LeadStatus, PaymentStatus } from "../../models/Lead"
import { Distances } from "../../pages/Calendar/TripManager"
import Lead from "../../pages/Leads/Lead"
import classNames from "../../utils/classNames"
import useLeads from "../../data/useLeads"
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers"
import useDriverTrackingLatest from "../../data/useDriverTrackingLatest"
import useMessages from "../../data/useMessages"
import { useAuth } from "../../contexts/AuthContext"
import { wilmaSAVCustomerId } from "../../config"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendar, faCommentSms, faCreditCard, faHashtag, faRoad, faTriangleExclamation, faTruck, faHourglass } from "@fortawesome/free-solid-svg-icons"
import Tooltip from "../shared/Tooltip"
import { formatCollectionOption, hasCollectionOption } from "../../utils/leadPaymentOption"
import { getLeadPostcode } from "../../utils/getLeadLocation"

interface ILeadEventProps {
  lead: ILead
  selected?: ILead | undefined
  handleSelected?: any
  distances?: Distances | undefined
  inGroup: number | undefined
  handleOrderChange: any
  searchTerm?: string
  tripColour?: string
  displayOrder?: number
}

export default function LeadEvent({ lead, selected, handleSelected, distances, inGroup, handleOrderChange, searchTerm, tripColour, displayOrder }: ILeadEventProps) {
  const queryClient = useQueryClient();
  const { userId, customerId } = useAuth();
  const isSavCustomer = !!customerId && customerId === wilmaSAVCustomerId;
  const { driverTrackingLatest } = useDriverTrackingLatest();
  const { unreadMessages } = useMessages();
  const { update } = useLeads(true, "LeadEvent");
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const [open, setOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<number>(lead.status);
  const [statusSaving, setStatusSaving] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const prevState = usePrevious(open);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: DragTypes.EVENT,
    item: lead,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    })
  }))

  function usePrevious(value: any) {
    const ref = useRef();

    useEffect(() => {
      ref.current = value;
    });

    return ref.current;
  }

  useEffect(() => {
    //After you close the lead modal, invalidate leads
    if (open === false && prevState === true) {
      queryClient.invalidateQueries(["leads"]);
    }
  }, [open])

  const handleOpenStatus = () => {
    setSelectedStatus(lead.status);
    setCancelMode(false);
    setCancelReason(lead.cancelledReason ?? "");
    setStatusOpen(true);
  };

  const handleSaveStatus = () => {
    if (selectedStatus === lead.status) {
      setStatusOpen(false);
      return;
    }

    setStatusSaving(true);
    update.mutate(
      { ...lead, status: selectedStatus },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setStatusOpen(false);
        },
        onError: () => {
          saveHandlers.onError();
        },
        onSettled: () => {
          setStatusSaving(false);
        },
      }
    );
  };

  const handleReinstate = () => {
    setStatusSaving(true);
    update.mutate(
      { ...lead, status: LeadStatus["Awaiting Contact"] },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setStatusOpen(false);
        },
        onError: () => {
          saveHandlers.onError();
        },
        onSettled: () => {
          setStatusSaving(false);
        },
      }
    );
  };

  const handleCancelLead = () => {
    const reason = cancelReason.trim();
    if (!reason) return;

    setStatusSaving(true);
    update.mutate(
      {
        ...lead,
        status: LeadStatus.Cancelled,
        cancelledReason: reason,
        cancelledBy: userId ?? null,
        cancelledOn: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setStatusOpen(false);
        },
        onError: () => {
          saveHandlers.onError();
        },
        onSettled: () => {
          setStatusSaving(false);
        },
      }
    );
  };

  const handleColour = () => {
    let background = "bg-red-200"

    if (lead.completedOn !== null || lead.status === LeadStatus["Completed"]) background = "bg-green-300";

    if (lead.status === LeadStatus["Collected"]) background = "bg-orange-300";
    if (lead.status === LeadStatus["Cancelled"]) background = "bg-red-500";

    if (lead.id == selected?.id) background = "bg-blue-200";

    if (distances !== undefined) {
      background = "bg-gray-300";

      if (distances?.green?.includes(lead.id)) {
        background = "bg-green-500";
      } else if (distances?.amber?.includes(lead.id)) {
        background = "bg-yellow-500";
      } else if (distances?.red?.includes(lead.id)) {
        background = "bg-red-500";
      }
    }

    if (searchTerm !== undefined && searchTerm.length > 0) {
      if (lead.vehicle?.vrm?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getLeadPostcode(lead).toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.contact?.postcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.contact?.fullname?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        background = "bg-yellow-200";
      }
    }

    if (lead.status === LeadStatus["Cancelled"]) background += " line-through";

    return background;
  }


  const isActive = useMemo(() => {
    if (driverTrackingLatest.data?.data) {
      const latestTracking = driverTrackingLatest.data.data.find(dt => dt.leadId === lead.id && dt.status === 1);
      return latestTracking !== undefined;
    }
    return false;
  }, [lead, driverTrackingLatest.data]);


  const unreadCount = useMemo(() => {
    return unreadMessages.data?.data.find(m => m.leadId === lead.id)?.unreadMessageCount ?? 0;
  }, [unreadMessages.data, lead.id]);


  const buildIcons = () => {
    let icons = [];

    //First in the row, and the only icon that is clickable - the point of the ticket is that a
    //reply to "we're collecting tomorrow" is easy to miss, so it should be the loudest thing on
    //the card and take one click to read.
    if (unreadCount > 0) {
      icons.push(
        <Tooltip content={`${unreadCount} unread text${unreadCount > 1 ? "s" : ""} - click to open`}>
          <span className="relative flex-none flex">
            {/* Ping ring sits behind the pill - the pill itself has to stay put and stay
                readable, so the animation goes on a copy of its shape rather than on it. */}
            <span className="absolute inset-0 rounded-full bg-red-500 opacity-75 animate-ping" />
            <button
              type="button"
              draggable={false}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
              title={`${unreadCount} unread text message(s)`}
              //A single pill rather than an icon with a floating count - the row is tight and a
              //badge hanging off the corner collides with the pickup number sat next to it.
              className="relative flex h-5 items-center gap-x-1 rounded-full bg-red-600 px-1.5 text-white"
            >
              <FontAwesomeIcon icon={faCommentSms} className="text-xs" />
              <span className="text-[10px] font-bold leading-none">{unreadCount}</span>
            </button>
          </span>
        </Tooltip>
      );
    }

    if (isActive) {
      icons.push(<Tooltip content="Pickup In Progress"><ArrowPathRoundedSquareIcon title="Pickup In Progress" className="h-5 w-5" /></Tooltip>);
    }

    if (lead.vehicle?.hasAlert) {
      icons.push(<Tooltip content="Vehicle Alert"><FontAwesomeIcon title="Vehicle Alert" icon={faTriangleExclamation} className="text-amber-500 text-lg" /></Tooltip>);
    }

    if (lead.vehicle?.isVan) {
      icons.push(<Tooltip content="Van"><FontAwesomeIcon title="Van" icon={faTruck} className="text-primary-600" /></Tooltip>);
    }

    if (lead.vehicle?.driveType === "4x4") {
      icons.push(<Tooltip content="4x4 Vehicle"><FontAwesomeIcon title="4x4 Vehicle" icon={faHashtag} className="text-primary-600 text-lg" /></Tooltip>);
    }

    if (hasCollectionOption(lead)) {
      const label = formatCollectionOption(lead);
      icons.push(<Tooltip content={label}><FontAwesomeIcon title={label} icon={faRoad} className="text-green-500 text-lg" /></Tooltip>);
    } else {
      if (lead.roadSidePayment) {
        icons.push(<Tooltip content="Roadside Payment"><FontAwesomeIcon title="Roadside Payment" icon={faRoad} className="text-green-500 text-lg" /></Tooltip>);
      }

      if (lead.fastPayment) {
        icons.push(<Tooltip content="Same Day Payment"><FontAwesomeIcon title="Same Day Payment" icon={faHourglass} className="text-green-500 text-lg" /></Tooltip>);
      }
    }

    const paymentStatusClass = (() => {
      switch (lead.paymentStatus) {
        case PaymentStatus.Raised:
          return "text-blue-500";
        case PaymentStatus.Sent:
          return "text-green-500";
        case PaymentStatus.Failed:
          return "text-red-500";
        case PaymentStatus.Approved:
          return "text-emerald-600";
        default:
          return "text-gray-500";
      }
    })();

    const paymentStatusLabel = PaymentStatus[lead.paymentStatus] ?? "Unknown";
    icons.push(
      <Tooltip content={`${paymentStatusLabel}`}>
        <FontAwesomeIcon
          title={`Payment Status: ${paymentStatusLabel}`}
          icon={faCreditCard}
          className={`${paymentStatusClass} text-lg`}
        />
      </Tooltip>
    );

    return icons;
  }


  return (
    <>
      <Modal open={open} setOpen={setOpen} width="max-w-6xl">
        <Lead modalLeadId={lead.id} setOpen={setOpen} />
      </Modal>

      <Modal open={statusOpen} setOpen={setStatusOpen} width="max-w-md">
        <div className="space-y-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {cancelMode ? "Cancel Lead" : "Change Status"}
          </h3>
          <p className="text-sm text-gray-500">
            {`${lead.vehicle?.vrm ?? ""}${lead.vehicle?.vrm ? ", " : ""}${getLeadPostcode(lead)}`}
          </p>

          {cancelMode ? (
            <>
              <p className="text-sm text-gray-500">
                Please enter a reason for cancelling this lead.
              </p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter cancellation reason..."
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  onClick={() => setCancelMode(false)}
                  disabled={statusSaving}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="rounded-md border border-transparent bg-red-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-60"
                  onClick={handleCancelLead}
                  disabled={statusSaving || !cancelReason.trim()}
                >
                  {statusSaving ? "Cancelling..." : "Cancel Lead"}
                </button>
              </div>
            </>
          ) : lead.status === LeadStatus.Cancelled ? (
            <>
              {lead.cancelledReason && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">Cancelled reason: </span>
                  {lead.cancelledReason}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  onClick={() => setStatusOpen(false)}
                  disabled={statusSaving}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="rounded-md border border-transparent bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                  onClick={handleReinstate}
                  disabled={statusSaving}
                >
                  {statusSaving ? "Reinstating..." : "Reinstate Lead"}
                </button>
              </div>
            </>
          ) : (
            <>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(parseInt(e.target.value))}
                className="w-full border border-gray-400 rounded-md text-sm"
              >
                {Object.values(LeadStatus)
                  .filter((ct) => isNaN(Number(ct)))
                  .filter((status) => status !== "All" && status !== "Cancelled")
                  // Only SAV hands leads to another yard, so only SAV can set Allocated
                  .filter((status) => isSavCustomer || status !== "Allocated")
                  .map((status) => (
                    <option
                      key={status}
                      value={LeadStatus[status as keyof typeof LeadStatus]}
                    >
                      {status}
                    </option>
                  ))}
              </select>
              <div className="flex justify-between gap-2">
                <button
                  type="button"
                  className="rounded-md border border-transparent bg-amber-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-amber-700 disabled:opacity-60"
                  onClick={() => setCancelMode(true)}
                  disabled={statusSaving}
                >
                  Cancel Lead
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                    onClick={() => setStatusOpen(false)}
                    disabled={statusSaving}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-60"
                    onClick={handleSaveStatus}
                    disabled={statusSaving}
                  >
                    {statusSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>

      <div
        className={classNames(handleColour(), " col-span-1 flex justify-between items-center mx-1 text-sm p-1 rounded-md mb-2 cursor-default")}
        ref={drag}
        onClick={() => handleSelected(lead)}
        style={{
          opacity: isDragging ? 0.8 : 1,
          borderLeft: tripColour ? `4px solid ${tripColour}` : undefined,
        }}
      >
        <div className="min-w-0">
          <span className="flex gap-x-1 items-center">
            {/* Pickup number leads the icon row (kept out of a separate column to save width). */}
            {displayOrder != null && (
              <span
                className="flex-none flex h-5 w-5 items-center justify-center rounded-full text-white text-xs font-bold"
                style={{ backgroundColor: tripColour ?? "#6b7280" }}
              >
                {displayOrder}
              </span>
            )}
            {buildIcons().map((icon, index) => (
              <span key={index} className="">
                {icon}
              </span>
            ))}
          </span>
          <span>
            {`${lead.vehicle?.vrm ?? ""}, ${getLeadPostcode(lead)} `}
          </span>
        </div>

        {/* <span>
          {lead.order}
        </span> */}

        <div className="flex">

          {(inGroup !== undefined && inGroup > 1) && (
            <>
              <button
                type="button"
                draggable={false}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOrderChange(true, lead);
                }}
              >
                <ArrowUpIcon className="h-4 w-4" />
              </button>

              <button
                type="button"
                draggable={false}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOrderChange(false, lead);
                }}
              >
                <ArrowDownIcon className="h-4 w-4" />
              </button>
            </>
          )}

<button
  type="button"
  title="Change Status"
  draggable={false}
  onMouseDown={(e) => e.stopPropagation()}
  onPointerDown={(e) => e.stopPropagation()}
  onClick={(e) => {
    e.stopPropagation();
    handleOpenStatus();
  }}
>
  <PencilSquareIcon className="h-5 w-5" />
</button>

<button
  type="button"
  draggable={false}
  onMouseDown={(e) => e.stopPropagation()}
  onPointerDown={(e) => e.stopPropagation()}
  onClick={(e) => {
    e.stopPropagation();
    setOpen(true);
  }}
>
  <ArrowTopRightOnSquareIcon className="h-5 w-5" />
</button>
        </div>
      </div >
    </>
  )
}